import { rollDie, type Rng } from './rng';

/**
 * I dadi speciali — secondo asse di build (brief §4.4), 10 al v1.
 * Grammatica visiva in art-direction §4: corpo = identità, pip = comportamento.
 */
export type DieKind =
  | 'standard'
  | 'pari'      // tira solo 2/4/6
  | 'bomba'     // +10 chips quando il suo 6 finisce in combo
  | 'specchio'  // copia il vicino di sinistra
  | 'piombato'  // 60% di ripetere l'ultima faccia
  | 'd8'        // facce 1-8
  | 'vetro'     // pips doppi, si rompe dopo 3 combo
  | 'magnete'   // assume la faccia più comune del tavolo
  | 'brace'     // +2 chips quando finisce in combo
  | 'truccato'  // non tira mai 1
  | 'caos';     // tira due volte e tiene il meglio

export const DIE_PRICE: Record<Exclude<DieKind, 'standard'>, number> = {
  brace: 5,
  pari: 5,
  piombato: 6,
  truccato: 6,
  specchio: 7,
  magnete: 7,
  bomba: 7,
  vetro: 8,
  caos: 8,
  d8: 9,
};

/** Bonus chips per ogni Dado Bomba usato in combo che mostra un 6. */
export const BOMB_BONUS = 10;
/** Bonus chips per ogni Dado di Brace usato in combo. */
export const EMBER_BONUS = 2;
/** Probabilità del Dado Piombato di ripetere l'ultima faccia. */
export const LOADED_REPEAT = 0.6;
/** Il Dado di Vetro regge questo numero di combo prima di rompersi. */
export const GLASS_USES = 3;

export type Chaos = 'none' | 'worse' | 'better';

function rollOne(kind: DieKind, lastFace: number, rng: Rng): number {
  switch (kind) {
    case 'pari':
      return 2 * rollDie(rng, 3); // 2, 4 o 6
    case 'piombato':
      return lastFace >= 1 && rng() < LOADED_REPEAT ? lastFace : rollDie(rng);
    case 'd8':
      return rollDie(rng, 8);
    case 'truccato':
      return 1 + rollDie(rng, 5); // 2-6
    case 'caos': {
      const a = rollDie(rng);
      const b = rollDie(rng);
      return Math.max(a, b);
    }
    default:
      return rollDie(rng);
  }
}

/**
 * Lancia i dadi non tenuti. Deterministico: l'unica fonte di casualità è rng.
 * - chaos 'worse'/'better' (TILT pieno): ogni dado lanciato tira due volte e
 *   prende la faccia peggiore/migliore — il tavolo sbanda, o ti asseconda.
 * - Il Dado Specchio copia il vicino di sinistra (sinistra→destra, in
 *   posizione 0 tiene il proprio tiro); il Magnete si aggancia alla faccia
 *   più comune del tavolo dopo che tutti gli altri si sono fermati.
 */
export function rollDiceSet(opts: {
  kinds: DieKind[];
  prevFaces: number[];
  held: boolean[];
  rng: Rng;
  chaos: Chaos;
}): number[] {
  const { kinds, prevFaces, held, rng, chaos } = opts;
  const faces = prevFaces.slice();
  const isDeferred = (k: DieKind) => k === 'specchio' || k === 'magnete';
  for (let i = 0; i < kinds.length; i++) {
    if (held[i]) continue;
    const kind = kinds[i] ?? 'standard';
    if (isDeferred(kind)) continue;
    let face = rollOne(kind, prevFaces[i] ?? 0, rng);
    if (chaos !== 'none') {
      const second = rollOne(kind, prevFaces[i] ?? 0, rng);
      face = chaos === 'worse' ? Math.min(face, second) : Math.max(face, second);
    }
    faces[i] = face;
  }
  // Specchio: copia il vicino di sinistra (già risolto se non-deferred).
  for (let i = 0; i < kinds.length; i++) {
    if (held[i] || kinds[i] !== 'specchio') continue;
    faces[i] = i > 0 && !isDeferred(kinds[i - 1] ?? 'standard') ? (faces[i - 1] ?? rollDie(rng)) : rollDie(rng);
  }
  // Magnete: faccia più comune tra i dadi risolti (a parità vince la più alta).
  for (let i = 0; i < kinds.length; i++) {
    if (held[i] || kinds[i] !== 'magnete') continue;
    const counts = new Map<number, number>();
    for (let j = 0; j < faces.length; j++) {
      if (j === i || (kinds[j] === 'magnete' && !held[j])) continue;
      const f = faces[j] ?? 0;
      if (f >= 1) counts.set(f, (counts.get(f) ?? 0) + 1);
    }
    let bestFace = rollDie(rng);
    let bestCount = 0;
    for (const [f, n] of counts) {
      if (n > bestCount || (n === bestCount && f > bestFace)) {
        bestFace = f;
        bestCount = n;
      }
    }
    faces[i] = bestFace;
  }
  return faces;
}
